import type { FloorPath } from "building-types/floors/floorTypes";
import type { CurbEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { CurbType } from "building-types/foundations/foundationEnums";
import {
  type FoundationPath,
  type FoundationSectionSize,
  FoundationSectionType,
} from "building-types/foundations/foundationTypes";
import type { Level } from "building-types/levels/levelTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { Table } from "common/data/table";
import {
  isGarageFoundationCondition,
  isPorchFoundationCondition,
  isExteriorFoundationCondition,
} from "doc/utils/foundationSystemTools";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { floorPathTop } from "../floor/floorPathUtils";
import type { FoundationPathEnds } from "./foundationStrip";
import {
  foundationPathFloorPerimeterEnds,
  foundationPathOffsetAlignment,
  foundationPathOpenings,
  foundationSectionId,
  linearFoundationSectionSizeFromAlignment,
} from "./foundationStrip";

export function makeCurbSectionSizes({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  curb,
  doorSettings,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  curb: CurbEntity;
  doorSettings: DoorSettings;
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  const { condition } = foundationPath;

  let roomClass: RoomClass | undefined;

  if (isGarageFoundationCondition[condition]) {
    roomClass = RoomClass.GARAGE;
  } else if (isPorchFoundationCondition[condition]) {
    roomClass = RoomClass.PORCH;
  } else if (isExteriorFoundationCondition[condition]) {
    roomClass = RoomClass.EXTERIOR;
  }

  if (roomClass !== undefined && floorPath) {
    const sectionCode = RoomClass[roomClass];
    const sectionId = foundationSectionId(curb.componentType, FoundationSectionType.COMPONENT, sectionCode);
    const bottom = floorPathTop(level, floorPath);
    const curbSize = makeCurbSize({
      foundationPath,
      floorPath,
      pathEnds,
      curb,
      sectionId,
      top: level.floorElevation,
      bottom: bottom,
      doorSettings,
      planarGraphs,
    });
    return [curbSize];
  }

  return [];
}

function makeCurbSize({
  foundationPath,
  floorPath,
  pathEnds,
  curb,
  sectionId,
  top,
  bottom,
  gapOpeningAdjustment,
  doorSettings,
  planarGraphs,
}: {
  foundationPath: FoundationPath;
  floorPath: FloorPath;
  pathEnds: FoundationPathEnds;
  curb: CurbEntity;
  sectionId?: string;
  top: number;
  bottom: number;
  gapOpeningAdjustment?: number;
  doorSettings: DoorSettings;
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { width } = curbWidth({ floorPath, curb });
  const depth = top - bottom;

  const { offset, alignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });
  const { start, end } = foundationPathFloorPerimeterEnds({
    foundationPath,
    floorPath,
    ends: pathEnds,
    planarGraphs,
  });
  const { openings } = foundationPathOpenings({
    foundationPath,
    floorPath,
    gapOpeningAdjustment,
    doorSettings,
    planarGraphs,
  });

  return linearFoundationSectionSizeFromAlignment({
    sectionComponent: curb,
    sectionType: FoundationSectionType.COMPONENT,
    sectionId,
    top,
    depth,
    width,
    alignment,
    offset,
    start,
    end,
    openings,
  });
}

export function curbWidth({
  floorPath,
  curb,
}: {
  floorPath: Pick<FloorPath, "leftWallOffset" | "rightWallOffset">;
  curb: CurbEntity;
}) {
  if (curb.curbType === CurbType.FIXED_WIDTH) {
    return { width: curb.curbWidth };
  }

  const { leftWallOffset, rightWallOffset } = floorPath;
  const width = leftWallOffset + rightWallOffset;

  return { width };
}
